import type { ThemeMode } from '@figma/fpl-themes';
import type { ThemePreference } from '@/types/auth.types';

/**
 * Weave's default and only shipped color mode prior to the `light-mode` rollout. Every fallback path
 * resolves here so an unset, corrupt, or unreadable preference can never change today's appearance.
 */
export const DEFAULT_THEME_MODE: ThemeMode = 'dark';

/**
 * `localStorage` mirror of the server-side `theme` user preference.
 *
 * The server copy is authoritative, but it only arrives after auth resolves. Reading the mode from
 * storage lets the very first paint use the right mode instead of flashing dark, and it gives
 * pre-auth surfaces (sign-in, standalone page loader) a mode at all.
 */
export const THEME_MODE_STORAGE_KEY = 'weave.themeMode';

function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

/**
 * Reads the mirrored appearance preference. Returns `null` when nothing is stored or the stored value isn't a
 * recognized preference, so callers can distinguish "never chose" from "chose dark".
 *
 * Storage access is wrapped because Safari throws on `localStorage` access in private mode and some
 * embed contexts block it outright; a theme preference is never worth breaking boot over.
 */
export function readStoredThemeMode(): ThemePreference | null {
  try {
    const stored = window.localStorage.getItem(THEME_MODE_STORAGE_KEY);
    return isThemePreference(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** Mirrors the appearance preference to storage. Silently no-ops when storage is unavailable. */
export function writeStoredThemeMode(mode: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
  } catch {
    // Ignored — the server preference is authoritative; the mirror is only a first-paint optimization.
  }
}

/**
 * Drops the mirror.
 *
 * Called when the flag turns off, because pre-provider surfaces (see `StandalonePageLoader`) read
 * storage without being able to check the flag. A stale `light` left behind by an earlier flag-on
 * session would make those surfaces render light while the flagged-off app renders dark.
 */
export function clearStoredThemeMode(): void {
  try {
    window.localStorage.removeItem(THEME_MODE_STORAGE_KEY);
  } catch {
    // Ignored — see writeStoredThemeMode.
  }
}

/**
 * Resolves the mode to render in, given the flag state and the available preference sources.
 *
 * Order matters:
 * 1. Flag off → `dark`, unconditionally. This is what makes the rollout safe: no stored preference
 *    or embed state can produce a light render until the flag turns on.
 * 2. Figma embed → `dark`. Figma sends no theme signal over postMessage today, so the embedded app
 *    stays black/white to match the surrounding UI3 chrome rather than guessing.
 * 3. The user's preference, then the storage mirror, then `dark`. System follows the device mode.
 */
export function resolveThemeMode({
  isLightModeFlagEnabled,
  isFigmaEmbed,
  preferredMode,
  storedMode,
  systemMode = DEFAULT_THEME_MODE,
}: {
  isLightModeFlagEnabled: boolean;
  isFigmaEmbed: boolean;
  preferredMode?: ThemePreference | null;
  storedMode?: ThemePreference | null;
  systemMode?: ThemeMode;
}): ThemeMode {
  if (!isLightModeFlagEnabled || isFigmaEmbed) {
    return DEFAULT_THEME_MODE;
  }

  return resolveThemePreference(preferredMode ?? storedMode, systemMode);
}

/** Maps a saved choice to the concrete mode required by FPL. */
export function resolveThemePreference(
  preference: ThemePreference | null | undefined,
  systemMode: ThemeMode,
): ThemeMode {
  return preference === 'system' ? systemMode : (preference ?? DEFAULT_THEME_MODE);
}

export const SYSTEM_THEME_QUERY = '(prefers-color-scheme: dark)';

/** Reads the browser's current device appearance before the first paint. */
export function readSystemThemeMode(): ThemeMode {
  return window.matchMedia(SYSTEM_THEME_QUERY).matches ? 'dark' : 'light';
}
